import React, { useState, useEffect } from 'react';
import { complaintAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import { Link } from 'react-router-dom';
import PageTitle from '../shared/PageTitle';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const StatusButtonGroup = ({ status, complaintId, onStatusChange }) => {
  return (
    <div className="btn-group btn-group-sm" role="group">
      <button
        type="button"
        className={`btn ${status === 'open' ? 'btn-danger' : 'btn-outline-danger'}`}
        onClick={() => onStatusChange(complaintId, 'open')}
        disabled={status === 'open'}
        title="Open"
      >
        <i className="fas fa-exclamation-circle me-1"></i> Open
      </button>
      <button
        type="button"
        className={`btn ${status === 'in_progress' ? 'btn-warning' : 'btn-outline-warning'}`}
        onClick={() => onStatusChange(complaintId, 'in_progress')}
        disabled={status === 'in_progress'}
        title="In Progress"
      >
        <i className="fas fa-tools me-1"></i> In Progress
      </button>
      <button
        type="button"
        className={`btn ${status === 'resolved' ? 'btn-success' : 'btn-outline-success'}`}
        onClick={() => onStatusChange(complaintId, 'resolved')}
        disabled={status === 'resolved'}
        title="Resolved"
      >
        <i className="fas fa-check me-1"></i> Resolved
      </button>
    </div>
  );
};

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { settings } = useSettings();

  // Get dwelling unit name
  const unitSingular = formatDwellingUnit(settings);
  // const unitPlural = formatDwellingUnit(settings, null, true);

  // For complaint details/response
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [responseText, setResponseText] = useState('');

  // For filtering
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredComplaints.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredComplaints.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('complaints-table-section');
    }, 50);
  };

  // Previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(() => {
        scrollToSectionTop('complaints-table-section');
      }, 50);
    }
  };

  // Next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(() => {
        scrollToSectionTop('complaints-table-section');
      }, 50);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);


  // Fetch complaints
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching complaints for admin...');

        // First try the standard complaints endpoint
        let complaintsData = [];
        try {
          const complaintsRes = await complaintAPI.getAllComplaints({
            status: filterStatus === 'all' ? '' : filterStatus,
            category: filterCategory === 'all' ? '' : filterCategory
          });
          console.log('Standard API response:', complaintsRes.data);
          complaintsData = complaintsRes.data;
        } catch (err) {
          console.error('Error with standard endpoint, trying owner endpoint:', err);
          // If standard endpoint fails, try the owner-specific endpoint
          const ownerRes = await fetch('/api/complaints/owner', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const ownerData = await ownerRes.json();
          console.log('Owner endpoint response:', ownerData);
          complaintsData = ownerData;
        }

        // If we still have no data, it means no complaints were found or an error occurred earlier
        if (!complaintsData || !Array.isArray(complaintsData)) {
          // Ensure complaintsData is an empty array if API calls failed or returned invalid data
          complaintsData = [];
        }

        // Initialize responses array if not present
        complaintsData = complaintsData.map(complaint => ({
          ...complaint,
          responses: complaint.responses || []
        }));

        setComplaints(complaintsData);
        setFilteredComplaints(complaintsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching complaints data:', err);
        setError('Failed to load complaints. Please check the API connection and try again.');
        // Ensure complaintsData is cleared or set to a state that indicates failure
        setComplaints([]);
        setFilteredComplaints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterStatus, filterCategory]);

  // Apply filters when filterStatus, filterCategory, or searchTerm changes
  useEffect(() => {
    let results = [...complaints];

    // Apply status filter
    if (filterStatus !== 'all') {
      results = results.filter(complaint => complaint.status === filterStatus);
    }

    // Apply category filter
    if (filterCategory !== 'all') {
      results = results.filter(complaint => complaint.category === filterCategory);
    }

    // Apply search filter
    if (searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase();
      results = results.filter(complaint =>
        (complaint.subject && complaint.subject.toLowerCase().includes(search)) ||
        (complaint.title && complaint.title.toLowerCase().includes(search)) ||
        (complaint.description && complaint.description.toLowerCase().includes(search)) ||
        (complaint.villa_number && complaint.villa_number.toLowerCase().includes(search)) ||
        (complaint.unit_type && complaint.unit_type.toLowerCase().includes(search)) ||
        (complaint.owner_name && complaint.owner_name.toLowerCase().includes(search))
      );
    }

    setFilteredComplaints(results);
  }, [complaints, filterStatus, filterCategory, searchTerm]);

  // Apply Bootstrap dropdown activation after component mounts
  useEffect(() => {
    // Enable Bootstrap dropdowns
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const dropdownElementList = document.querySelectorAll('.dropdown-toggle');
      if (dropdownElementList.length > 0 && window.bootstrap && window.bootstrap.Dropdown) {
        // const dropdownList = [...dropdownElementList].map(dropdownToggleEl => 
        //   new window.bootstrap.Dropdown(dropdownToggleEl)
        // );
      }
    }
  }, [filteredComplaints]);

  // After the existing useEffect for dropdowns, add a new effect to configure dropdown placement
  useEffect(() => {
    // Configure dropdowns to always display below the button
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Wait for Bootstrap to be fully loaded
      setTimeout(() => {
        const statusDropdowns = document.querySelectorAll('.dropdown-status-menu .dropdown-toggle');
        if (statusDropdowns.length > 0 && window.bootstrap && window.bootstrap.Dropdown) {
          statusDropdowns.forEach(dropdownEl => {
            // Get existing dropdown instance or create new one
            let dropdown = window.bootstrap.Dropdown.getInstance(dropdownEl);
            if (dropdown) {
              dropdown.dispose(); // Remove existing instance
            }

            // Create with custom config
            dropdown = new window.bootstrap.Dropdown(dropdownEl, {
              popperConfig: {
                placement: 'bottom-end',
                modifiers: [
                  {
                    name: 'flip',
                    enabled: false // Disable flipping to top when there's no space
                  },
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 2], // Add some spacing below the button
                    },
                  }
                ]
              }
            });

            // Store dropdown instance on the element
            dropdownEl._dropdown = dropdown;
          });
        }
      }, 100);
    }
  }, [filteredComplaints, showModal]);

  // Add event listeners to ensure dropdown position after component mounts
  useEffect(() => {
    const positionDropdownMenu = (e) => {
      const button = e.target;
      const menu = button.nextElementSibling;
      if (menu && menu.classList.contains('dropdown-menu')) {
        // Force position below the button
        const buttonRect = button.getBoundingClientRect();
        menu.style.position = 'fixed';
        menu.style.top = `${buttonRect.bottom + 2}px`;
        menu.style.left = 'auto';
        menu.style.right = `${window.innerWidth - buttonRect.right}px`;
        menu.style.transform = 'none';
        menu.style.inset = 'auto';

        // Prevent Bootstrap from repositioning
        const stopPropagation = (event) => event.stopPropagation();
        menu.addEventListener('transitionstart', stopPropagation, { once: true });
        menu.addEventListener('transitionend', stopPropagation, { once: true });
      }
    };

    // Attach listeners
    const dropdownButtons = document.querySelectorAll('.dropdown-status-menu .dropdown-toggle');
    dropdownButtons.forEach(button => {
      button.addEventListener('show.bs.dropdown', positionDropdownMenu);
    });

    // Clean up
    return () => {
      const buttons = document.querySelectorAll('.dropdown-status-menu .dropdown-toggle');
      buttons.forEach(button => {
        button.removeEventListener('show.bs.dropdown', positionDropdownMenu);
      });
    };
  }, [filteredComplaints, showModal]);

  // Add a useEffect to inject global style overrides for dropdowns
  useEffect(() => {
    // Create style element if it doesn't exist
    let styleEl = document.getElementById('dropdown-force-override');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'dropdown-force-override';
      styleEl.innerHTML = `
        /* Force all dropdowns to appear below their triggers */
        .dropdown-menu.show {
          position: absolute !important;
          top: 100% !important;
          bottom: auto !important;
          left: auto !important;
          right: 0 !important;
          transform: none !important;
          margin-top: 2px !important;
          z-index: 9999 !important;
        }
        
        /* Reset inset property which can cause positioning issues */
        .dropdown-menu.show[style*="inset:"] {
          inset: auto !important;
        }
        
        /* Ensure dropdown menus are visible */
        .dropdown-menu.show {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Override specific dropdown menu positioning for status dropdown */
        .dropdown-status-menu .dropdown-menu.show {
          min-width: 200px !important;
          left: auto !important;
          right: 0 !important;
          top: 100% !important;
        }
      `;
      document.head.appendChild(styleEl);
    }

    return () => {
      // Don't remove the style element on unmount to ensure consistent behavior
    };
  }, []);

  const handleOpenModal = (complaint) => {
    setSelectedComplaint(complaint);
    setResponseText('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedComplaint(null);
    setResponseText('');
  };

  function formatToMySQLDateTime(date) {
    const pad = (n) => (n < 10 ? '0' + n : n);

    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1);
    const day = pad(date.getUTCDate());
    const hour = pad(date.getUTCHours());
    const minute = pad(date.getUTCMinutes());
    const second = pad(date.getUTCSeconds());

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }

  const handleAddResponse = async () => {
    if (!responseText.trim()) return;

    try {
      // Save response to API and get the response object from backend
      const apiRes = await complaintAPI.addResponse(selectedComplaint.id, { text: responseText });
      console.log('Response added to API successfully', apiRes);

      // Use the response object from backend (adjust path as per your API)
      const newResponse = apiRes.data?.response || {
        text: responseText,
        created_by: 'Admin User',
        created_at: formatToMySQLDateTime(new Date())
      };

      const updatedComplaints = complaints.map(complaint => {
        if (complaint.id === selectedComplaint.id) {
          const updatedComplaint = {
            ...complaint,
            responses: [...complaint.responses, newResponse],
            updated_at: formatToMySQLDateTime(new Date())
          };

          setSelectedComplaint(updatedComplaint);
          return updatedComplaint;
        }
        return complaint;
      });

      setComplaints(updatedComplaints);
      setResponseText('');
    } catch (error) {
      console.error('Error adding response:', error);
      alert('Failed to save response. Please try again.');
    }
  };

  const handleStatusChange = async (complaintId, newStatus) => {
    try {
      console.log(`Updating complaint ${complaintId} status to ${newStatus}`);

      // Call API to update complaint status
      await complaintAPI.updateStatus(complaintId, newStatus);
      console.log('Status updated successfully');

      // Update complaint status in state
      const updatedComplaints = complaints.map(complaint => {
        if (complaint.id === complaintId) {
          const updatedComplaint = {
            ...complaint,
            status: newStatus,
            updated_at: formatToMySQLDateTime(new Date())
          };

          if (selectedComplaint && selectedComplaint.id === complaintId) {
            setSelectedComplaint(updatedComplaint);
          }

          return updatedComplaint;
        }
        return complaint;
      });

      setComplaints(updatedComplaints);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  function formatDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return 'N/A';

    try {
      // Parse "YYYY-MM-DD HH:mm:ss" as UTC
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);

      // Create a UTC Date object
      const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

      // Format to IST using timeZone
      return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(utcDate);
    } catch (err) {
      console.error('Invalid date string:', dateString, err);
      return 'Invalid Date';
    }
  }

  // const getStatusBadgeClass = (status) => {
  //   switch (status) {
  //     case 'open':
  //       return <span className="badge bg-danger">Open</span>;
  //     case 'in_progress':
  //       return <span className="badge bg-warning text-dark">In Progress</span>;
  //     case 'resolved':
  //       return <span className="badge bg-success">Resolved</span>;
  //     default:
  //       return <span className="badge bg-secondary">Unknown</span>;
  //   }
  // };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'maintenance':
        return <span className="badge bg-info">Maintenance</span>;
      case 'security':
        return <span className="badge bg-danger">Security</span>;
      case 'other':
        return <span className="badge bg-secondary">Other</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  if (loading && complaints.length === 0) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <PageTitle icon="fa-exclamation-circle" title="Complaints / Suggestions Management" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading complaints...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle
        icon="fa-exclamation-circle"
        title="Complaints / Suggestions Management"
        action={
          <Link to="/admin/dashboard" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
          </Link>
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by subject, description or unit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                  >
                    <option value="all">All Categories</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="security">Security</option>
                    <option value="parking">Parking</option>
                    <option value="noise">Noise</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complaints Table */}
      <div className="card" id="complaints-table-section">
        <div className="card-header">
          <h5 className="mb-0">
            All Complaints / Suggestions
            <span className="badge bg-secondary ms-2">{filteredComplaints.length}</span>
          </h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Subject</th>
                  <th>{unitSingular}</th>
                  <th>Category</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredComplaints.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-3">
                      <i className="fas fa-info-circle me-2"></i>
                      No complaints / Suggestions found matching your filters.
                    </td>
                  </tr>
                ) : (
                  currentItems.map(complaint => (
                    <tr key={complaint.id}>
                      <td>#{complaint.id}</td>
                      <td>{complaint.subject || complaint.title}</td>
                      <td>
                        {complaint.unit_type ? `${complaint.unit_type} ` : ''}
                        {complaint.villa_number}
                      </td>
                      <td>{getCategoryBadge(complaint.category)}</td>
                      <td>{formatDate(complaint.created_at)}</td>
                      <td>
                        <div className="statusButtonsGroup d-inline-block">
                          <StatusButtonGroup
                            status={complaint.status}
                            complaintId={complaint.id}
                            onStatusChange={handleStatusChange}
                          />
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleOpenModal(complaint)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {currentItems.length > 0 && (
            <div className="card-footer d-flex justify-content-between align-items-center">
              <div>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredComplaints.length)} of {filteredComplaints.length} complaints / suggestions
              </div>

              <nav aria-label="Visitor pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={previousPage} disabled={currentPage === 1}>
                      <i className="fas fa-chevron-left"></i>
                    </button>
                  </li>

                  {/* Simplified pagination - show only certain page numbers */}
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNumber = index + 1;

                    // Show first page, last page, current page, and pages around current page
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => paginate(pageNumber)}>
                            {pageNumber}
                          </button>
                        </li>
                      );
                    }

                    // Show ellipsis
                    if (
                      pageNumber === 2 && currentPage > 3 ||
                      pageNumber === totalPages - 1 && currentPage < totalPages - 2
                    ) {
                      return (
                        <li key={pageNumber} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }

                    return null;
                  })}

                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={nextPage} disabled={currentPage === totalPages}>
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* View Complaint Modal */}
      {showModal && selectedComplaint && (
        <div>
          <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1080 }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                  <h5 className="modal-title">
                    Complaint / Suggestion #{selectedComplaint.id} - {selectedComplaint.subject || selectedComplaint.title}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <h6>Details</h6>
                      <div className="mb-2">
                        <strong>{unitSingular}:</strong>
                        {selectedComplaint.unit_type ? `${selectedComplaint.unit_type} ` : ''}
                        {selectedComplaint.villa_number}
                      </div>
                      {selectedComplaint.owner_name && (
                        <div className="mb-2">
                          <strong>Filed By:</strong> {selectedComplaint.owner_name}
                        </div>
                      )}
                      <div className="mb-2">
                        <strong>Category:</strong> {getCategoryBadge(selectedComplaint.category)}
                      </div>
                      <div className="mb-2">
                        <strong>Status:</strong>
                        <div className="statusButtonsGroup d-inline-block ms-2">
                          <StatusButtonGroup
                            status={selectedComplaint.status}
                            complaintId={selectedComplaint.id}
                            onStatusChange={handleStatusChange}
                          />
                        </div>
                      </div>
                      <div className="mb-2">
                        <strong>Date Filed:</strong> {formatDate(selectedComplaint.created_at)}
                      </div>
                      <div className="mb-2">
                        <strong>Last Updated:</strong> {formatDate(selectedComplaint.updated_at)}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h6>Description</h6>
                      <div className="border rounded p-3 bg-light">
                        {selectedComplaint.description || "No description provided."}
                      </div>
                    </div>
                  </div>

                  {/* Responses Section */}
                  <div className="mt-4">
                    <h6>Responses</h6>

                    {selectedComplaint.responses && selectedComplaint.responses.length > 0 ? (
                      <div className="responses-list">
                        {selectedComplaint.responses.map((response, index) => (
                          <div key={index} className="card mb-2">
                            <div className="card-body py-2">
                              <div className="d-flex justify-content-between">
                                <strong>{response.created_by || 'Admin'}</strong>
                                <span className="text-muted small">
                                  {formatDate(response.created_at)}
                                </span>
                              </div>
                              <p className="mb-0 mt-1">{response.text}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle me-2"></i>
                        No responses yet.
                      </div>
                    )}

                    {/* Add response form */}
                    <div className="mt-3">
                      <div className="mb-3">
                        <label htmlFor="responseText" className="form-label">Add Response</label>
                        <textarea
                          className="form-control"
                          id="responseText"
                          rows="3"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          placeholder="Enter your response here..."
                        ></textarea>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddResponse}
                        disabled={!responseText.trim()}
                      >
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Response
                      </button>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <div className="d-flex w-100 justify-content-between">
                    <div>
                      {/* Remove the old Mark as Resolved button since we now have the dropdown */}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" style={{ zIndex: 1070 }}></div>
        </div>
      )}
    </div>
  );
};

export default Complaints; 